import React, { forwardRef } from 'react';

interface FilterCardProps {
    /** Card title */
    title: string;
    /** Optional icon to display before the title */
    icon?: React.ReactNode;
    /** Content to render inside the card */
    children: React.ReactNode;
    /** Optional badge count to display */
    badge?: number;
    /** Optional ID for scroll targeting */
    id?: string;
}

/**
 * A reusable card wrapper for filter sections in the FilterPanel.
 * Provides consistent styling with white background, rounded corners, and subtle shadow.
 */
export const FilterCard = forwardRef<HTMLDivElement, FilterCardProps>(({
    title,
    icon,
    children,
    badge,
    id,
}, ref) => {
    return (
        <div
            ref={ref}
            id={id}
            className="bg-white rounded-xl border border-[#e5e5eb] shadow-sm overflow-hidden"
        >
            {/* Header bar */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#e5e5eb] bg-[#fafafc]">
                {icon && (
                    <span className="text-[#6d6d8a]">
                        {icon}
                    </span>
                )}
                <h4 className="text-xs font-semibold uppercase tracking-wide text-[#6d6d8a]">
                    {title}
                </h4>
                {badge !== undefined && badge > 0 && (
                    <span className="inline-flex items-center justify-center rounded-full bg-[#8c8ca3] px-2 py-0.5 text-[10px] font-medium text-white">
                        {badge}
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="p-4">
                {children}
            </div>
        </div>
    );
});

FilterCard.displayName = 'FilterCard';

export default FilterCard;
